import { Button, OtpInput } from '@bennows/boows-react';
import { useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, call } from '../../lib/api';
import { CODE_LENGTH, useCodeStep } from './useCodeStep';

/** Step 2 of the sign-in: the code from the authenticator app (REQ-140-004); `onUsePasskey` offers the way back to the passkey. */
export function CodeStep({ onVerified, onStartOver, onUsePasskey }: { readonly onVerified: () => void; readonly onStartOver: () => void; readonly onUsePasskey?: () => void }) {
  const { t } = useTranslation();
  const step = useCodeStep((code) => call(() => api.POST('/v1/operator/sessions/current/second-factor', { body: { code } })), onVerified);
  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (step.failure) messageRef.current?.focus();
  }, [step.failure]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    step.submit();
  };

  return (
    <>
      <h1>{t('signIn.codeTitle')}</h1>
      <p className="ui-auth__lead">{t('signIn.codeLead')}</p>
      <form onSubmit={onSubmit} noValidate>
        <OtpInput
          label={t('signIn.code')}
          value={step.code}
          onChange={step.setCode}
          onComplete={(value) => step.submit(value)}
          length={CODE_LENGTH}
          groupSize={3}
          autoFocus
          disabled={step.pending}
          invalid={step.incomplete || step.failure !== null}
          help={step.incomplete ? t('signIn.codeIncomplete') : undefined}
        />
        {step.failure ? (
          <p className="ui-auth__message" role="alert" tabIndex={-1} ref={messageRef}>
            {t(step.failure)}
          </p>
        ) : null}
        <Button type="submit" loading={step.pending} block>
          {t('signIn.codeSubmit')}
        </Button>
      </form>
      {onUsePasskey ? (
        <Button variant="secondary" onClick={onUsePasskey} block>
          {t('signIn.usePasskey')}
        </Button>
      ) : null}
      <Button variant="ghost" onClick={onStartOver}>
        {t('signIn.startOver')}
      </Button>
    </>
  );
}
